/**
 * Safe parsers for Finlytics statement cells.
 *
 * Raw values are strings that may include commas, signs, percent marks, or blanks.
 * Missing / non-numeric input must stay null — never coerced to 0.
 */

const METADATA_KEYS = new Set(['isExpandable', 'setAttributes', 'class']);

/**
 * True when a key is a fiscal period, not statement metadata.
 * Accepts 4-digit years (19xx / 20xx) and TTM variants.
 *
 * @param {unknown} key
 * @returns {boolean}
 */
export function isPeriodKey(key) {
    if (key == null) return false;
    const str = String(key).trim();
    if (!str || METADATA_KEYS.has(str)) return false;
    if (/^TTM/i.test(str)) return true;
    return /^(19|20)\d{2}$/.test(str);
}

/**
 * Normalize a period key to a canonical year (`"2024"`) or `"TTM"`.
 *
 * @param {unknown} key
 * @returns {string|null}
 */
export function normalizePeriodKey(key) {
    if (!isPeriodKey(key)) return null;
    const str = String(key).trim();
    if (/^TTM/i.test(str)) return 'TTM';
    const match = str.match(/(19|20)\d{2}/);
    return match ? match[0] : null;
}

/**
 * Parse a monetary / share / signed cash-flow cell.
 *
 * @param {unknown} raw
 * @returns {number|null} Finite number, or null when blank / non-numeric.
 *
 * @example parseCurrencyValue("5,059") => 5059
 * @example parseCurrencyValue("-1,148") => -1148
 * @example parseCurrencyValue("-0") => 0
 * @example parseCurrencyValue("") => null
 */
export function parseCurrencyValue(raw) {
    if (raw == null) return null;
    if (typeof raw === 'number') {
        if (!Number.isFinite(raw)) return null;
        return raw === 0 ? 0 : raw;
    }
    if (typeof raw === 'object') {
        if (Object.prototype.hasOwnProperty.call(raw, 'value')) {
            return parseCurrencyValue(raw.value);
        }
        if (Object.prototype.hasOwnProperty.call(raw, '$numberDouble')) {
            return parseCurrencyValue(raw.$numberDouble);
        }
        if (Object.prototype.hasOwnProperty.call(raw, '$numberInt')) {
            return parseCurrencyValue(raw.$numberInt);
        }
        return null;
    }

    const str = String(raw).trim();
    if (!str) return null;

    const cleaned = str.replace(/[,₹\s]/g, '').replace(/[^0-9.-]/g, '');
    if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.') return null;

    const n = Number(cleaned);
    if (!Number.isFinite(n)) return null;
    return n === 0 ? 0 : n;
}

/**
 * Parse a percentage-formatted source field using percentage-point convention.
 * `"18%"` and `18` both become `18` (not 0.18), matching API ratio values
 * such as fcfMargin `11.83` and existing ChartModal `%` stripping.
 *
 * Never feed a percentage source into a monetary calculation.
 *
 * @param {unknown} raw
 * @returns {number|null}
 *
 * @example parsePercentValue("18%") => 18
 */
export function parsePercentValue(raw) {
    const n = parseCurrencyValue(raw);
    return n;
}

/**
 * Parse a raw cell according to the metric unit.
 *
 * @param {unknown} raw
 * @param {string} [unit]
 * @returns {number|null}
 */
export function parseMetricValue(raw, unit) {
    if (unit === 'percentage') return parsePercentValue(raw);
    return parseCurrencyValue(raw);
}

/**
 * Safe division for ratios / multiples.
 * Returns null when the denominator is 0, null, undefined, or not finite,
 * or when the numerator is null / not finite.
 *
 * @param {number|null|undefined} numerator
 * @param {number|null|undefined} denominator
 * @returns {number|null}
 */
export function safeDivide(numerator, denominator) {
    if (numerator == null || denominator == null) return null;
    if (!Number.isFinite(numerator) || !Number.isFinite(denominator)) return null;
    if (denominator === 0) return null;
    const result = numerator / denominator;
    return Number.isFinite(result) ? result : null;
}

/**
 * Sum signed cash-flow / monetary inputs. Any null / non-finite addend yields null.
 *
 * @param {...(number|null|undefined)} values
 * @returns {number|null}
 */
export function safeSum(...values) {
    let total = 0;
    for (const value of values) {
        if (value == null || !Number.isFinite(value)) return null;
        total += value;
    }
    return total === 0 ? 0 : total;
}

/**
 * Absolute value for display or coverage math. Null stays null.
 *
 * @param {number|null|undefined} value
 * @returns {number|null}
 */
export function absoluteValue(value) {
    if (value == null || !Number.isFinite(value)) return null;
    const abs = Math.abs(value);
    return abs === 0 ? 0 : abs;
}
